import React, { useState, useEffect, useRef } from 'react';
import AccountStarIcon from 'mdi-react/AccountStarIcon';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import {
  Button, Input, notification, Form, Modal, Col, Row,
  Checkbox, Card, Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './LoginComponent.scss';
import { WarningOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { namespaces } from '../../../i18n/i18n.constants';
import {
  removeUserFromStorage, setUserInfoOnLocalStorage, checkUserIsAuth,
} from '../../../utils/users';

import { encrypty } from '../../../utils/helpers';
import {
  get, put, post, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { GENERIC_ERROR_MESSAGE } from '../../../utils/responseMessages';
import { formatBP } from '../../../utils/format';
import TokenUserValidationComponent from '../TokenUserValidationComponent/TokenUserValidationComponent';
import { updateLinkPolicyAndCookiesUrl, updateLinkLGPD } from '../../../redux/actions/app';

const LoginComponent = (): JSX.Element => {
  interface LoginForm {
    email: string;
    federalRegistration: string;
    password: string;
    termsAccepted: boolean;
  }

  interface SignInArgs {
    appVersion?: string;
    bp?: string;
    device?: string;
    email: string,
    federalRegistration?: string,
    password: string,
    termsAccepted: boolean,
    type: string
  }

  const dispatch = useDispatch();

  const { t } = useTranslation('namespaces');
  const [formInstance] = Form.useForm();
  const [formAceitoInstance] = Form.useForm();
  const navigate = useNavigate();
  const targetFederalRegistration = useRef('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isBPInformed, setIsBPInformed] = useState<boolean>(false);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [, setResponseValue] = useState([]);
  const [aceitoCheckValue, setAceitoCheckValue] = useState<boolean>(false);
  const [valuesState, setValuesState] = useState<LoginForm>({
    federalRegistration: '',
    email: '',
    password: '',
    termsAccepted: false,
  });
  const [modalChoiceTypeUser, setModalChoiceTypeUser] = useState<boolean>(false);
  const [objectRequestAuth, setObjectRequestAuth] = useState<SignInArgs>({
    email: '',
    password: '',
    termsAccepted: false,
    type: '',
  });
  const [elementsValues, setElementsValues] = useState<LoginForm>({
    federalRegistration: '',
    email: '',
    password: '',
    termsAccepted: false,
  });
  const [typeUserSelected, setTypeUserSelected] = useState<string | null>(null);
  const [visibleModalValidation, setVisibleModalValidation] = useState<boolean>(false);
  const [objectDataUserToken, setObjectDataUserToken] = useState<{
    federalRegistration: string,
    email: string,
    phone: string,
    responseCodeToken: number,
  }>({
    federalRegistration: '',
    email: '',
    phone: '',
    responseCodeToken: 0,
  });
  let transportDataUserToken: {
    federalRegistration: string,
    email: string,
    phone: string,
  } = {
    federalRegistration: '',
    email: '',
    phone: '',
  };

  function getHref(linkToHelp: string | null): string {
    if (linkToHelp === null) {
      return '#';
    }
    return linkToHelp;
  }

  const downloadPDF = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = get({
      url: '/file/downloadTermsAndConditions',

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      noAuth: true,

      responseType: 'blob',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.status === 200): {
        const filee = new Blob([response?.body], { type: 'application/pdf' });

        const linkElement = document.createElement('a');
        const url = window.URL.createObjectURL(filee);

        linkElement.href = url;
        linkElement.download = 'Termos e Condições';

        document.body.append(linkElement);

        linkElement.click();

        setTimeout(() => {
          linkElement.remove();
          window.URL.revokeObjectURL(url);
        }, 0);
      } break;

      case (responseError?.body?.code === -9002):
        // "File not found", -9002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Documento não encontrado.',
        });
        break;

      case (response?.body?.code === 8002):
        // "No terms and conditions saved", 8002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Não existem documentos de termos e condições cadastrados.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Não foi possivel fazer o download do arquivo.',
        });
        break;
    }
  };

  const showModal = (): void => {
    setVisibleModal(true);
  };

  /**
   * @name showModalToken
   * @param {bool} newStatus
   * @description gerencia a visualização da modal
   * @returns mostra ou esconde as informações da modal para interação com o usuário
   */
  const showModalToken = (newStatus: boolean): void => {
    setVisibleModalValidation(newStatus);

    if (newStatus === false) {
      setObjectDataUserToken({
        federalRegistration: '',
        email: '',
        phone: '',
        responseCodeToken: 0,
      });
    }
  };

  const [objectLinkForHelp, setObjectLinkForHelp] = useState<{
    needHelp: boolean | null,
    linkToHelp: string | null,
  }>({
    linkToHelp: null,
    needHelp: null,
  });

  useEffect(() => {
    if (checkUserIsAuth()) {
      navigate('/');
    }

    return () => {
      setLoading(false);
    };
  });

  const changeCheckAceito = (value: { target: { checked: any; }; }): void => {
    const v = value.target.checked;
    setAceitoCheckValue(v);
  };

  const getLinkForHelp = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = get({
      url: '/system/getLinkForHelp',

      frontConfigs: {
        navigateFunction: navigate,
      },

      noAuth: true,
    });

    try {
      response = await requestReference;
    } catch (excep: unknown) {
      if (isSuperAgentError(excep)) {
        responseError = excep.response;
      } else {
        genericError = excep as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 8006):
        // "link need help retrieved successfully", 8006
        setObjectLinkForHelp(response?.body?.getLinkForHelpOutput);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        setObjectLinkForHelp({
          needHelp: null,
          linkToHelp: null,
        });
        break;
    }
  };

  useEffect(() => {
    getLinkForHelp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEmailVerification = async (a: string): Promise<void> => {
    notification.destroy();

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = get({
      url: `/auth/customer/resendEmailConfirmation/${a}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      noAuth: true,
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1013):
        // "Resend email confirmation successfully", 1013
        notification.success({
          message: t('success', { ns: namespaces.common }),
          description: 'E-mail enviado. Verifique sua caixa de entrada.',
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Usuário não encontrado.',
        });
        break;

      case (responseError?.body?.code === -1014):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Este email já está confirmado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, houve um erro ao executar esta ação, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const loginFunction = (
    response: {
      name: string;
      bp: string;
      id: number;
      token: string;
      role: {
        name: string;
        type: string;
        permissions: any;
      };
      userMail: any;
    }): void => {
    // Encrypty and save user ID in local storage
    // localStorage.setItem('user_id', encrypty(response.data.id));

    // Save user data in the local storage
    setUserInfoOnLocalStorage(response);

    // Encrypty and save data in local storage
    localStorage.setItem('user_name', response.name ? encrypty(response.name) : encrypty('-'));
    localStorage.setItem('user_bp', response.bp ? encrypty(response.bp) : encrypty(''));
    localStorage.setItem('user_id', response.id ? encrypty(`${response.id}`) : encrypty(''));
    localStorage.setItem('cooper_token', encrypty(response.token));
    localStorage.setItem('user_role', encrypty(response.role.name));
    localStorage.setItem('user_type', encrypty(response.role.type));
    localStorage.setItem('user_permissions', encrypty(JSON.stringify(response.role.permissions)));

    localStorage.setItem('user_mail', encrypty(JSON.stringify(response.userMail)));

    // Change page
    navigate('/');
  };

  const sendTokenVerification = async (doc: string): Promise<void> => {
    const args = {
      federalRegistration: doc,
    };

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError;

    const requestReference: RequestWrapper = put({
      url: '/auth/sendTokenTwoFactor',
      params: args,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1019):
        // "Token send successfully", 1019
        setObjectDataUserToken({
          ...transportDataUserToken,
          responseCodeToken: 1019,
        });

        notification.success({
          message: 'Token gerado!',
          description: 'Um código de validação foi enviado.',
        });
        break;

      case (response?.body?.code === 1020):
        // "Telephone token send successfully", 1020
        setObjectDataUserToken({
          ...transportDataUserToken,
          responseCodeToken: 1020,
        });

        notification.success({
          message: 'Token gerado!',
          description: 'Um código de validação foi enviado.',
        });
        break;

      case (response?.body?.code === 1021):
        // "Email token send successfully", 1021
        setObjectDataUserToken({
          ...transportDataUserToken,
          responseCodeToken: 1021,
        });

        notification.success({
          message: 'Token gerado!',
          description: 'Um código de validação foi enviado.',
        });
        break;

      case (responseError?.body?.code === -1021):
        // "Federal registration not found in sap database", -1021
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'CPF/CNPJ não encontrado',
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Usuário não encontrado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Desculpe, houve um erro ao executar esta solicitação.',
        });
        break;
    }
  };

  const modalConfirmation = (msg:string): void => {
    removeUserFromStorage();
    setModalChoiceTypeUser(false);
    setTypeUserSelected(null);
    Modal.confirm({
      title: 'Atenção',
      content: msg,
      okText: 'Ok',
      okCancel: false,
      autoFocusButton: null,
      icon: (<WarningOutlined />),
      width: 500,
    });
  };



const handleBpInfo = (
  params = {
    bpRegistration: '',
    federalRegistration: '',
    type: 'federalRegistration',
    password: ''
  },
): RequestWrapper => {
  const requestPayload: {
    PARTNER?: string,
    CPF_CNPJ?: string,
  } = {};
  const allowedTypes = ['bp', 'federalRegistration'];

  if (!params?.bpRegistration && !params.federalRegistration) {
    throw new Error('Erro. BP ou documento não informado.');
  }

  if (!params?.type) {
    throw new Error('Erro. Tipo de pesquisa não informado.');
  }

  if (!allowedTypes.includes(params.type)) {
    throw new Error(`Erro. Tipo inválido: "${params.type}". Tipos de pesquisa disponiveis: ${allowedTypes.join(', ')}.`);
  }

  switch (true) {
    case (params.type === 'bp'):
      // 0001136253 | 0001002068
      requestPayload.PARTNER = params.bpRegistration;
      break;

    case (params.type === 'federalRegistration'):
    default:
      // 00831373003715 | 04269667834
      requestPayload.CPF_CNPJ = params.federalRegistration;
      break;
  }


  const requestReference: RequestWrapper = post({
    url: '/auth/infoBp/',
    params: params,

    noAuth: true,

    frontConfigs: {
      navigateFunction: navigate,
    },
  });

  return requestReference;
  
};


  /**
 * @name handleAuthMonitora
 * @param {*} args
 * @param {*} values
 * @description verifica e autoriza a entrada do usuário na aplicação
 * @returns retorna o usuário autenticado para acessar a aplicação
 */
  const handleAuthMonitora = async (args: SignInArgs, values: LoginForm): Promise<void> => {
    setLoading(true);
    let newArgs = args;

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const bp = (values.federalRegistration.length === 10 ? values.federalRegistration : '');
    const cpfCnpj = (values.federalRegistration.length > 10 ? values.federalRegistration : '');

    if (typeUserSelected) {
      newArgs.type = typeUserSelected;
    }

    if (values?.termsAccepted) {
      newArgs.termsAccepted = true;
    }

    if (!newArgs?.email) {
      newArgs = {
        email: values.email,
        password: values.password,
        termsAccepted: values.termsAccepted,
        type: args.type,
      };
    }

    if (args.type === 'COOPERCITRUS') {
      if (bp) {
        newArgs.bp = bp;
      }

      if (cpfCnpj) {
        newArgs.federalRegistration = cpfCnpj;
      }
    }

    if (args.type === 'COOPERADO') {
      newArgs.federalRegistration = cpfCnpj;
    }

    const requestReference: RequestWrapper = post({
      url: '/auth/signin/',
      params: newArgs,

      noAuth: true,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }

      setLoading(false);
    }

    switch (true) {
      case (response?.body?.code === 1002):
        // "User logged in successfully", 1002
        response.body.userMail = values?.email?.toLowerCase();
        setResponseValue(response?.body);
        loginFunction(response?.body);
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1001):
        notification.error({
          message: 'E-mail e/ou senha inválidos',
          description: 'Verifique e tente novamente.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1022):
        // Email and telephone not found in sap database
        notification.error({
          message: 'Dados inconsistentes!',
          description: 'Campo e-mail e telefone vazio, procure a filial mais próxima para atualizar os seus dados cadastrais.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1016):
        // "Invalid federal registration or password", -1016
        notification.error({
          message: 'Autenticação inválida!',
          description: 'Os dados informados estão incorretos.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1019):
        // "Federal registration already linked to another user", -1019
        notification.error({
          message: 'Autenticação cancelada!',
          description: 'Número de registro vinculado a outro usuário.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1025):
        // "Token not validated", -1025
        // trigger token to user
        sendTokenVerification(targetFederalRegistration.current);

        setVisibleModalValidation(true);
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -19000):
        // "SAP unavailable", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19001):
        // "SAP unavailable", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19002):
        // "SAP unavailable", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1012):
        // "Customer not activated", -1012
        notification.error({
          message: 'Usuário inativo',
          description: 'Entre em contato com a sua filial.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1013):
        // "Admin not activated", -1013
        notification.error({
          message: 'Usuário inativo',
          description: 'Entre em contato com o administrador do sistema.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -8002):
        // "Terms and conditions of use cannot be null", -8002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Não existem termos e condiçoes cadastrados.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1015):
        // "current document pending acceptance", -1015
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        setValuesState(values);
        showModal();
        break;

      case (responseError?.body?.code === -8003):
        // "Need to update the app", -8003
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1028):
        // "For Cooperated the federal registration must be informed", -1028
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Para Cooperado deve ser informado o CPF/CNPJ.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1029):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'O acesso é permitido somente para cooperados, funcionários e estagiários.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;  
      
      case (responseError?.body?.code === -1033):
        modalConfirmation('Sua conta está bloqueada devido a solicitação de exclusão.');

        break;
      case (responseError?.body?.code === -1037):
        modalConfirmation('Sua conta foi excluída.');
        break;

      case (responseError?.body?.code === -1041):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'CPF não registrado no campo digital. É necessário criar uma conta.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1042):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'BP ou CPF deve ser informado.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;   

      case (responseError?.body?.code === -1043):
          notification.error({
            message: t('error', { ns: namespaces.common }),
            description: 'Documento ainda não cadastrado no campo digital. É necessário criar uma conta.',
          });
          removeUserFromStorage();
          setModalChoiceTypeUser(false);
          setTypeUserSelected(null);
          break; 
          
      case (responseError?.body?.code === -1044):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'BP não encontrado no banco de dados.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;

      case (responseError?.body?.code === -1046):
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Usuário com mais de um registro ativo.',
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;
          

      case (responseError?.body?.apiResponse?.code === -25005): {
        notification.error({
          message: 'Conexão com SAP',
          description: 'SAP excedeu o tempo limite de resposta para solicitação',
        });
        break;
      }

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: GENERIC_ERROR_MESSAGE,
        });
        removeUserFromStorage();
        setModalChoiceTypeUser(false);
        setTypeUserSelected(null);
        break;
    }

    setLoading(false);
  };

  const handleSubmit = async (values: LoginForm): Promise<void> => {
    localStorage.setItem('cookiesAccepted', 'true');
    setLoading(true);

    const args: SignInArgs = {};

    args.password = values.password;

    const bp = (values.federalRegistration.length === 10 ? values.federalRegistration : '');
    const cpfCnpj = (values.federalRegistration.length > 10 ? values.federalRegistration : '');

    if (values.federalRegistration.length === 10) {
      setIsBPInformed(true);
    } else {
      setIsBPInformed(false);
    }

    try {
      const requestRef: RequestWrapper = handleBpInfo({
        bpRegistration: bp,
        federalRegistration: cpfCnpj,
        type: cpfCnpj != '' ? 'federalRegistration' : 'bp',
        password: values.password
      });
      
      const responseSAP = await requestRef;

      args.type = (responseSAP.body.type === 'FUNCIONÁRIO' ? 'COOPERCITRUS' : responseSAP.body.type);
      targetFederalRegistration.current = responseSAP.body.federalRegistration.toString();
      transportDataUserToken = {
        federalRegistration: responseSAP.body.federalRegistration.toString(),
        email: responseSAP.body.email.toString(),
        phone: responseSAP.body.telephone.toString(),
      };
    } catch (ex: unknown) {
      console.log(ex);
      console.log(ex?.response.body.code);
      setLoading(false);
      setModalChoiceTypeUser(false);
      setTypeUserSelected(null);

      if (ex.response === undefined || ex?.response?.body.code === -19000 ) {
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });

        return;
      }
      if (ex?.response?.body.code === -19001 ) {
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'Sem comunicação com o SAP - Alto tempo de espera ou servidor está inacessível.',
        });

        return;
      }
      if (ex?.response?.body.code === -19002 ) {
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'ERRO 400 - Erro em ponto de comunicação',
        });

        return;
      }

      if (ex?.response?.body.code === -19003 ) {
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: 'ERRO 500 - Erro interno no servidor',
        });

        return;
      }

      notification.error({
        message: t('error', { ns: namespaces.common }),
        description: 'CPF/CNPJ/BP não encontrado na base da Coopercitrus.',
      });
      return;
    }

    if (args.type !== '') {
      setObjectRequestAuth(args);
      if (args.type === 'COOPERADO/FUNCIONÁRIO') {
        setElementsValues(values);
        setModalChoiceTypeUser(true);
      } else {
        handleAuthMonitora(args, values);
      }
    } else {
      setLoading(false);
      setModalChoiceTypeUser(false);
      setTypeUserSelected(null);

      notification.error({
        message: t('error', { ns: namespaces.common }),
        description: 'CPF/CNPJ/BP não encontrado na base da Coopercitrus.',
      });
    }

    setLoading(false);
  };

  return (
    <div className="LoginComponent">
      <Form
        scrollToFirstError
        form={formInstance}
        className="LoginComponent-form"
        onFinish={handleSubmit}
        autoComplete="on"
        noValidate
      >
        
            
        <Form.Item
          className="login-form"
          name="federalRegistration"
          rules={[
            {
              required: true,
              message: 'Por favor, insira seu CPF/CNPJ/BP e utilize somente números.',
            },
            {
              max: 14,
              message: 'O campo deve ter no máximo até 14 digitos.',
            },
            {
              pattern: /^[a-z,A-z,0-9,@,.,_,-]+$/,
              message: 'Não utilize caracteres especiais ou acentuados.',
            },
            {
              validator: (_rule, value): Promise<void | Error> => {
                const cleanValue = value.replace(/^0+/, '');

                if (cleanValue.length === 0) {
                  return Promise.reject(new Error('Por favor insira um CPF/CNPJ/BP válido.'));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            className="federalRegistration-input"
            type="number"
            maxLength={14}
            min={0}
            max={14}
            onBlur={
              (eventt): void => {
                if (eventt.target.value.length <= 10) {
                  formInstance.setFieldsValue({
                    federalRegistration: eventt.target.value.replace(/.*/, formatBP(eventt.target.value)),
                  });
                }
                formInstance.setFieldsValue({
                  federalRegistration: eventt.target.value.replace(/.*/, eventt.target.value),
                });
              }
            }
            placeholder="CPF/CNPJ/BP"
            autoComplete="federalRegistration"
          />
        </Form.Item>

        <Form.Item
          className="LoginComponent-form__item"
          name="password"
          rules={[
            {
              required: true,
              message: 'Por favor, insira sua senha',
            },
            {
              min: 6,
              message: 'As senhas devem ter no mínimo 6 digitos',
            },
          ]}
        >
          <Input
            name="password"
            className="login-input"
            type="password"
            placeholder="Senha"
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item
          className="LoginComponent-form__item"
        >
          <Button
            type="primary"
            htmlType="submit"
            className="btn-login"
            loading={loading}
          >
            ENTRAR
          </Button>
        </Form.Item>

        <Button
          onClick={
            (): void => {
              navigate('/forgot-password');
            }
          }
          type="link"
          className="link-forgot-password"
        >
          Esqueci a senha
        </Button>

        {
          objectLinkForHelp.needHelp === true
            ? (
              <Button
                type="link"
                className="link-forgot-password"
                href={getHref(objectLinkForHelp.linkToHelp)}
                target="_blank"
              >
                Precisa de ajuda?
              </Button>
            )
            : null
        }
      </Form>

      <Modal
        className="aceito-modal"
        title={<h3 style={{ margin: 0 }}>Termos e condições de uso</h3>}
        visible={visibleModal}
        onCancel={
          (): void => {
            setLoading(false);
            setResponseValue([]);
            setAceitoCheckValue(false);
            setVisibleModal(false);
          }
        }
        footer={[
          <Button
            className="btn-ok"
            type="primary"
            key="submit"
            onClick={
              (): void => {
                if (formAceitoInstance.getFieldValue('aceitoCheckbox')) {
                  valuesState.termsAccepted = true;
                  handleAuthMonitora(objectRequestAuth, valuesState);
                }
              }
            }
          >
            Salvar
          </Button>,
        ]}
      >
        <Col>
          <Row>
            O documento de termos e condições de uso foi atualizado.
            Por favor, realize o aceite do novo documento.
          </Row>
          <Row>
            <Form
              form={formAceitoInstance}
            >
              <Form.Item
                name="aceitoCheckbox"
              >
                <Row>
                  <Checkbox
                    value={aceitoCheckValue}
                    onChange={changeCheckAceito}
                    style={{ paddingRight: '1vw' }}
                  />
                  <Col>
                    Li e aceito os&nbsp;
                  </Col>
                  <Col>
                    <a
                      onKeyDown={
                        (eventt): void => {
                          if (eventt.key === 'Enter') {
                            downloadPDF();
                          }
                        }
                      }
                      tabIndex={0}
                      role="button"
                      onClick={
                        (): void => {
                          downloadPDF();
                        }
                      }
                      style={{
                        color: 'green',
                        cursor: 'pointer',
                      }}
                    >
                      termos e condições
                    </a>
                  </Col>
                  <Col>
                    &nbsp;de uso da Coopercitrus
                  </Col>
                </Row>
              </Form.Item>
            </Form>
          </Row>
        </Col>
      </Modal>

      <Modal
        centered
        title={<h3 style={{ margin: 0 }}>Escolha um tipo de usuário</h3>}
        visible={modalChoiceTypeUser}
        onCancel={
          (): void => {
            setLoading(false);
            setModalChoiceTypeUser(false);
            setTypeUserSelected(null);
          }
        }
        footer={[
          <Button
            className="btn-ok"
            type="primary"
            key="submit"
            onClick={
              (): void => {
                handleAuthMonitora(objectRequestAuth, elementsValues);
              }
            }
          >
            Continuar
          </Button>,
        ]}
      >
        <Row gutter={[24, 24]}>
          {
            isBPInformed === false
              ? (
                <Col span={12}>
                  <Card
                    key={1}
                    hoverable
                    className={
                      typeUserSelected === 'COOPERADO'
                        ? 'LoginComponent__ServiceCardSelected'
                        : 'LoginComponent__ServiceCard'
                    }
                    onClick={
                      (): void => {
                        setTypeUserSelected('COOPERADO');
                      }
                    }
                  >
                    <AccountGroupOutlineIcon className="LoginComponent__CardIcon" />
                    <p className="LoginComponent__CardTitle">COOPERADO</p>
                  </Card>
                </Col>
              )
              : null
          }
          <Col
            span={isBPInformed === true ? 24 : 12}
          >
            <Card
              key={2}
              hoverable
              className={typeUserSelected === 'COOPERCITRUS' ? 'LoginComponent__ServiceCardSelected' : 'LoginComponent__ServiceCard'}
              onClick={
                (): void => {
                  setTypeUserSelected('COOPERCITRUS');
                }
              }
            >
              <AccountStarIcon className="LoginComponent__CardIcon" />
              <p className="LoginComponent__CardTitle">COOPERCITRUS</p>
            </Card>
          </Col>
        </Row>

      </Modal>

      {
        objectDataUserToken.responseCodeToken != 0
          ? (
            <Modal
              centered
              title={<h3 style={{ margin: 0 }}>Validação de token</h3>}
              visible={visibleModalValidation}
              onCancel={
                (): void => {
                  setLoading(false);
                  setVisibleModalValidation(false);
                }
              }
              footer={null}
              destroyOnClose
            >
              <TokenUserValidationComponent
                targetValidation={objectDataUserToken}
                modalToken={showModalToken}
              />
            </Modal>
          )
          : null
      }
    </div>
  );
};

LoginComponent.propTypes = {};
LoginComponent.defaultProps = {};

export default LoginComponent;
